import React, { useEffect, useState } from "react";
import {Btn, CashCalculator, FormInput, PaymentItemCoupon, Toggle} from "./";
import { useCore } from "../hooks";
import { PaymentItem } from "depoto-core";
import { applyCashRounding, formatPrice, getPaymentAmountForType, round2 } from "../utils";

type PaymentTypes = 'cash' | 'card' | 'coupon' | 'combination'
type PaymentTabType = {
  label: string
  value: PaymentTypes
  icon: string
  amount: string
  isEnabled: boolean
}
const allPaymentTypes: PaymentTabType[] = [{
  label: 'Hotovost',
  value: 'cash',
  icon: 'wallet',
  amount: '0',
  isEnabled: false,
}, {
  label: 'Karta',
  value: 'card',
  icon: 'credit-card',
  amount: '0',
  isEnabled: false,
}, {
  label: 'Poukaz',
  value: 'coupon',
  icon: 'badge-percent',
  amount: '0',
  isEnabled: false,
}, {
  label: 'Kombinace',
  value: 'combination',
  icon: 'calculator-simple',
  amount: '0',
  isEnabled: false,
}
]
export const PaymentItemsColumn: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState<PaymentTypes>('cash')
  const [paymentTypes, setPaymentTypes] = useState<PaymentTabType[]>([allPaymentTypes.find(pt => pt.value === 'combination')])
  const { currentOrder, setCurrentOrder, checkouts, selectedCheckoutId, orderVouchers, currency } = useCore()
  // payment amounts are calculated from exact price, priceAll includes cash rounding
  const priceAllExact = currentOrder?.priceAllExact ?? currentOrder?.priceAll
  const amountForType = (paymentTypeId: string, amount: number) => getPaymentAmountForType(paymentTypeId, amount, currency?.id)

  // todo:
  // each paymentType have its own template part .. all the same input, except for coupon (btn with search by code, select and coupons rows...)
  // get available types from checkout.payments
  // decide if we filter types in here or in template; show only available
  // always show combination, if types.len > 1
  // update paymetnitems on order on tab change, combination toggle change, combination value change
  useEffect(() => {
    if (checkouts?.length && selectedCheckoutId) {
      const checkout = checkouts?.find(c => c.id === selectedCheckoutId)
      const availablePaymentTypes = checkout.payments?.map(p => p.type.id)
      availablePaymentTypes.push('combination')
      const nextPTs = [...allPaymentTypes.filter(pt => availablePaymentTypes.includes(pt.value))]
      nextPTs.forEach(pt => {
        const pItemForType = currentOrder.paymentItems?.find(pi => pi?.payment?.type?.id === pt.value)
        // console.log('enable this shit! on init', pItemForType)
        if (pItemForType) {
          pt.isEnabled = true
        }
      })
      setPaymentTypes(nextPTs)
      // we dont need currentOrder in dependencies, as we only need this to call on pageload!
      if (currentOrder?.paymentItems?.length === 1 && currentOrder.paymentItems[0].payment?.type?.id?.length) {
        setSelectedTab(currentOrder.paymentItems[0].payment.type.id as PaymentTypes)
      } else {
        // } if (currentOrder?.paymentItems?.length > 1) {
        setSelectedTab('cash')
      }
    }
  }, [checkouts, selectedCheckoutId])

  // set cash payment on first load of bill preview automatically
  useEffect(() => {
    if (currentOrder?.paymentItems?.length === 0 && selectedTab !== 'coupon' && selectedTab !== 'combination') {
      const paymentItems = []
      const checkout = checkouts?.find(c => c.id === selectedCheckoutId)
      const payment = checkout.payments?.[0]
      paymentItems.push(new PaymentItem({
        payment,
        amount: amountForType(payment?.type?.id, priceAllExact),
      }))
      updatePaymentItems(paymentItems)
    }
  }, [currentOrder])

  const onTabChange = (paymentType: PaymentTypes) => {
    const checkout = checkouts?.find(c => c.id === selectedCheckoutId)
    setSelectedTab(paymentType)
    const paymentItems = []
    const couponsSum = currentOrder.paymentItems
      ?.filter(pi => pi?.payment?.type?.id === 'coupon')
      ?.reduce((sum, pi) => sum + Number(pi?.amount || 0), 0) || 0
    const remainingAmount = Math.max(0, priceAllExact - couponsSum)
    const couponItems = currentOrder.paymentItems?.filter(
      pi => pi?.payment?.type?.id === 'coupon',
    ) || []
    if (paymentType === 'coupon') {
      updatePaymentItems(couponItems)
      return
    }
    if (paymentType !== "combination") {
      paymentItems.push(new PaymentItem({
        payment: checkout.payments?.find(p => p.type.id === paymentType),
        amount: amountForType(paymentType, remainingAmount),
      }))
      paymentItems.push(...couponItems)
    } else {
      const nextPaymentTypes = JSON.parse(JSON.stringify([...paymentTypes]))
      const firstCombinationVariant = nextPaymentTypes.find(
        (pt: PaymentTabType) => pt.value !== 'combination' && pt.value !== 'coupon',
      )
      if (firstCombinationVariant) {
        nextPaymentTypes.forEach((pt: PaymentTabType) => {
          if (pt.value === firstCombinationVariant.value) {
            pt.isEnabled = true
            pt.amount = `${amountForType(pt.value, remainingAmount)}`
            return
          }
          if (pt.value !== 'combination' && pt.value !== 'coupon') {
            pt.isEnabled = false
            pt.amount = '0'
          }
        })
        setPaymentTypes(nextPaymentTypes)
        paymentItems.push(
          new PaymentItem({
            payment: checkout.payments?.find(
              p => p.type.id === firstCombinationVariant.value,
            ),
            amount: amountForType(firstCombinationVariant.value, remainingAmount),
          }),
        )
        paymentItems.push(...couponItems)
      }
    }
    // console.log('onTabChange', {paymentItems, paymentType})
    updatePaymentItems(paymentItems)
  }

  const updatePaymentItems = (paymentItems?: PaymentItem[]) => {
    const nextOrder = { ...currentOrder }
    nextOrder.paymentItems = paymentItems?.length ? [...paymentItems] : []
    // console.log('upi', {paymentItems})
    setCurrentOrder(applyCashRounding(nextOrder, currency?.id))
  }

  const updatePaymentTypes = (paymentType: PaymentTypes, isEnabled?: boolean, amount?: string) => {
    const orderPrice = priceAllExact
    const totalWithoutCoupons = Math.max(0, orderPrice - getCouponsSum())
    const checkout = checkouts?.find(c => c.id === selectedCheckoutId);
    const nextPaymentTypes = JSON.parse(JSON.stringify([...paymentTypes]))
    const nextPaymentItems = []
    nextPaymentTypes.forEach(pt => {
      if (pt.value === paymentType) {
        if (typeof isEnabled === 'boolean') {
          pt.isEnabled = isEnabled
        }
        if (typeof amount === 'string') {
          pt.amount = amount
        }
      }
      if (pt.isEnabled && pt.value !== 'coupon') {
        nextPaymentItems.push(new PaymentItem({
          payment: checkout.payments?.find(p => p.type.id === pt.value),
          amount: Number(pt.amount),
        }));
      }
    })

    const enabledNonCouponTypes = nextPaymentTypes.filter(
      pt => pt.isEnabled && pt.value !== 'coupon' && pt.value !== 'combination',
    )
    const editedType = nextPaymentTypes.find(pt => pt.value === paymentType)
    const editedIsEnabled = !!editedType?.isEnabled
    const isToggleChange = typeof isEnabled === 'boolean'
    const hasEditedType = enabledNonCouponTypes.some(pt => pt.value === paymentType)
    const shouldRebalance =
      (hasEditedType && enabledNonCouponTypes.length > 1) ||
      (isToggleChange && !editedIsEnabled && enabledNonCouponTypes.length > 0)

    if (shouldRebalance) {
      const balancingType = isToggleChange && !editedIsEnabled
        ? enabledNonCouponTypes.find(pt => pt.value === 'cash') ||
          enabledNonCouponTypes[0]
        : enabledNonCouponTypes.find(pt => pt.value === 'cash' && pt.value !== paymentType) ||
          enabledNonCouponTypes.find(pt => pt.value !== paymentType)

      if (balancingType) {
        const sumWithoutBalancing = enabledNonCouponTypes
          .filter(pt => pt.value !== balancingType.value)
          .reduce((sum, pt) => sum + Number(pt.amount || 0), 0)
        const nextBalancingAmount = amountForType(balancingType.value, Math.max(0, totalWithoutCoupons - sumWithoutBalancing))
        balancingType.amount = `${nextBalancingAmount}`
      }
    }

    nextPaymentItems.length = 0
    nextPaymentTypes.forEach(pt => {
      if (pt.isEnabled && pt.value !== 'coupon' && pt.value !== 'combination') {
        nextPaymentItems.push(
          new PaymentItem({
            payment: checkout.payments?.find(p => p.type.id === pt.value),
            amount: Number(pt.amount),
          }),
        )
      }
    })
    const couponItems = currentOrder.paymentItems?.filter(
      pi => pi?.payment?.type?.id === 'coupon',
    ) || []
    nextPaymentItems.push(...couponItems)
    setPaymentTypes(nextPaymentTypes);
    updatePaymentItems(nextPaymentItems)
  }

  useEffect(() => {
    if (selectedTab !== 'combination') {
      return
    }

    const couponsSum = currentOrder.paymentItems
      ?.filter(pi => pi?.payment?.type?.id === 'coupon')
      ?.reduce((sum, pi) => sum + Number(pi?.amount || 0), 0) || 0
    const enabledNonCouponTypes = paymentTypes.filter(
      pt => pt.isEnabled && pt.value !== 'coupon' && pt.value !== 'combination',
    )
    const balancingType = enabledNonCouponTypes.find(pt => pt.value === 'cash') || enabledNonCouponTypes[0]
    if (!balancingType) {
      return
    }

    const sumWithoutBalancing = enabledNonCouponTypes
      .filter(pt => pt.value !== balancingType.value)
      .reduce((sum, pt) => sum + Number(pt.amount || 0), 0)
    const targetBalancingAmount = getPaymentAmountForType(balancingType.value, Math.max(0, priceAllExact - couponsSum - sumWithoutBalancing), currency?.id)
    const currentBalancingAmount = Number(balancingType.amount || 0)

    if (Math.abs(currentBalancingAmount - targetBalancingAmount) < 0.0001) {
      return
    }

    const nextPaymentTypes = paymentTypes.map(pt =>
      pt.value === balancingType.value ? { ...pt, amount: `${targetBalancingAmount}` } : pt,
    )
    const checkout = checkouts?.find(c => c.id === selectedCheckoutId)
    const nextPaymentItems = nextPaymentTypes
      .filter(pt => pt.isEnabled && pt.value !== 'coupon' && pt.value !== 'combination')
      .map(
        pt =>
          new PaymentItem({
            payment: checkout.payments?.find(p => p.type.id === pt.value),
            amount: Number(pt.amount),
          }),
      )
    const couponItems = currentOrder.paymentItems?.filter(
      pi => pi?.payment?.type?.id === 'coupon',
    ) || []

    setPaymentTypes(nextPaymentTypes)
    setCurrentOrder(applyCashRounding({
      ...currentOrder,
      paymentItems: [...nextPaymentItems, ...couponItems],
    }, currency?.id))
  }, [selectedTab, paymentTypes, currentOrder, checkouts, selectedCheckoutId, setCurrentOrder, priceAllExact, currency])

  const getCombinationSum = () => {
    return currentOrder?.paymentItems?.map(pi => pi.amount).reduce((a, b) => a + b, 0)
  }

  const getCouponsSum = () => {
    return currentOrder?.paymentItems
      ?.filter(pi => pi?.payment?.type?.id === 'coupon')
      ?.reduce((sum, pi) => sum + Number(pi?.amount || 0), 0) || 0
  }

  const CommonPaymentTypeRow = (ptr: PaymentTabType) => (
    <div className="flex flex-row gap-2 items-center justify-between" key={`payment-type-${ptr.value}`}>
      <div className="flex flex-row gap-2 items-center">
        <Toggle value={ptr.isEnabled} onChange={() => updatePaymentTypes(ptr.value, !ptr.isEnabled)} />
        <div>{ptr.label}</div>
      </div>
      <div>
        <FormInput type={'number'} id={`payment-type-input-${ptr.value}`} value={ptr.amount} onChange={e => updatePaymentTypes(ptr.value, undefined, e)} />
      </div>
    </div>
  )

  return (
    <div className="p-4">
      <div className="bg-white border-b-1 border-solid border-border-light rounded-md p-6 mb-6">
        <div className="flex flex-row justify-evenly rounded-md overflow-hidden">
          {paymentTypes.map(pt => (
            <Btn
              key={`payment-type-tab-${pt.value}`}
              className={`flex-1 xl:btn-lg rounded-none ${pt.value === selectedTab ? 'btn-primary' : 'btn-primary btn-soft'}`}
              icon={pt.icon}
              children={pt.label}
              onClick={() => onTabChange(pt.value)} />
          ))}
        </div>
        <div className="flex flex-col gap-2 mt-8">
          {selectedTab === 'cash' && (
            <div className="flex flex-col gap-2">
              <div className="flex flex-row justify-between font-bold text-2xl">
                <div>Platba v hotovosti:</div>
                <div className="font-bold">{formatPrice(amountForType('cash', Math.max(0, priceAllExact - getCouponsSum())), currency?.id)}</div>
              </div>
              <CashCalculator />
            </div>
          )}
          {selectedTab === 'card' && (
            <div className="flex flex-col gap-2">
              <div className="flex flex-row justify-between font-bold text-2xl">
                <div>Platba kartou:</div>
                <div className="font-bold">{formatPrice(round2(Math.max(0, priceAllExact - getCouponsSum())), currency?.id)}</div>
              </div>
            </div>
          )}
          {selectedTab === 'coupon' && (
            <div className="flex flex-col gap-2">
              {/*<div className="flex flex-row justify-between font-bold text-2xl">*/}
              {/*  <div>Platba poukazem:</div>*/}
              {/*  <div className="font-bold">{formatPrice(currentOrder.priceAll, currency?.id)}</div>*/}
              {/*</div>*/}
              <PaymentItemCoupon />
            </div>
          )}
          {selectedTab === 'combination' && (
            <div>
              <div className="flex flex-col gap-2">
                {paymentTypes
                  .find(ptr => ptr.value === 'coupon') ? (
                  <PaymentItemCoupon />
                ) : null
                }
              </div>
              <div className="mt-3 mb-2 font-bold text-xl">
                K rozdělení mezi další typy plateb zbývá: {formatPrice(round2(Math.max(0, priceAllExact - getCouponsSum())), currency?.id)}
              </div>
              <div className="flex flex-col gap-2">
                {paymentTypes
                  .filter(ptr => ptr.value !== 'combination' && ptr.value !== 'coupon')
                  .map(ptr => CommonPaymentTypeRow(ptr))}
              </div>
              <div className="flex flex-row justify-between font-bold text-2xl mt-4">
                <span>Součet:</span> <span>{formatPrice(getCombinationSum(), currency?.id)}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
